import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { trackEvent } from "@shared/lib/posthog";
import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";
import XIcon from "@ats/src/components/shared/XIcon";
import Tooltip from "@ats/src/components/shared/Tooltip";

import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useToastContext } from "@shared/context/ToastContext";

type Props = {
  jobId: string;
  jobTitle: string;
  jobUrl: string;
  onCancel: () => void;
};

function JobPublishSuccessModal(props: Props) {
  const { jobId, jobTitle, jobUrl, onCancel } = props;
  const { currentOrganization } = useCurrentSession();
  const addToast = useToastContext();

  const hasTracked = useRef(false);
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackEvent("job_publish_success_modal_shown", { job_id: jobId, job_title: jobTitle });
  }, [jobId, jobTitle]);

  const linkedInEnabled =
    currentOrganization?.eligibleForLinkedin && currentOrganization?.linkedinCompanyId;
  const xHiringEnabled = currentOrganization?.enableXHiring;

  // Build distribution message based on what's enabled
  const getDistributionMessage = () => {
    if (!linkedInEnabled) {
      return null;
    }

    if (xHiringEnabled) {
      return "Your job is being distributed to thousands of applicants across LinkedIn, X Hiring, and Google Jobs!";
    }

    return "Your job is being distributed to thousands of applicants across LinkedIn and Google Jobs!";
  };

  const distributionMessage = getDistributionMessage();

  // Social share handlers
  const handleShareToTwitter = () => {
    const companyName = currentOrganization?.name;
    const tweet = `${companyName} is hiring a ${jobTitle}`;
    const twitterShareUrl = `${jobUrl}?source=twitter`;
    const tweetParam = encodeURIComponent(tweet);
    window.open(`https://x.com/intent/post?text=${tweetParam}&url=${twitterShareUrl}`, "_blank");
  };

  const handleShareToLinkedIn = () => {
    const linkedinShareUrl = `${jobUrl}?source=linkedin`;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${linkedinShareUrl}`,
      "_blank",
    );
  };

  const handleCopyLink = () => {
    addToast({ title: "Link copied", kind: "success" });
  };

  // Build full URL for external link buttons
  const buildFullUrl = (path: string) => `${window.location.origin}${path}`;

  return (
    <CenterModal headerTitleText="Job Published 🎉" onCancel={onCancel}>
      {distributionMessage && (
        <Styled.DistributionMessage>{distributionMessage}</Styled.DistributionMessage>
      )}

      <Styled.ModalSection>
        <p>Share this job across your network to expand your reach:</p>

        <Styled.ShareIconsContainer>
          <Tooltip label="Share on X">
            <Styled.ShareIconButton onClick={handleShareToTwitter}>
              <XIcon />
            </Styled.ShareIconButton>
          </Tooltip>

          <Tooltip label="Share on LinkedIn">
            <Styled.ShareIconButton onClick={handleShareToLinkedIn}>
              <Icon name="linkedin" />
            </Styled.ShareIconButton>
          </Tooltip>

          <Tooltip label="Copy link">
            <CopyToClipboard text={jobUrl} onCopy={handleCopyLink}>
              <Styled.ShareIconButton>
                <Icon name="link" />
              </Styled.ShareIconButton>
            </CopyToClipboard>
          </Tooltip>
        </Styled.ShareIconsContainer>
      </Styled.ModalSection>
      <Styled.ModalSection>
        <p>Some things worth setting up to maximize your job's visibility:</p>

        <Styled.ActionList>
          <Styled.ActionItem>
            {linkedInEnabled ? (
              <>
                Double check{" "}
                <Styled.ActionLink
                  href={buildFullUrl(`/jobs/${jobId}/distribution/linkedin`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  LinkedIn and X Hiring
                </Styled.ActionLink>{" "}
                requirements for this job
              </>
            ) : xHiringEnabled ? (
              <>
                <Styled.ActionLink
                  href={buildFullUrl(`/hire/settings/integrations/linkedin`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Enable LinkedIn
                </Styled.ActionLink>{" "}
                to reach thousands of applicants
              </>
            ) : (
              <>
                <Styled.ActionLink
                  href={buildFullUrl(`/hire/settings/integrations/linkedin`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Enable LinkedIn and X Hiring
                </Styled.ActionLink>{" "}
                to reach thousands of applicants
              </>
            )}
          </Styled.ActionItem>

          <Styled.ActionItem>
            Distribute this job to{" "}
            <Styled.ActionLink
              href={buildFullUrl(`/jobs/${jobId}/distribution/weworkremotely`)}
              target="_blank"
              rel="noreferrer"
            >
              We Work Remotely or WhatJobs
            </Styled.ActionLink>
          </Styled.ActionItem>

          <Styled.ActionItem>
            Customize the{" "}
            <Styled.ActionLink
              href={buildFullUrl(`/jobs/${jobId}/setup/automations`)}
              target="_blank"
              rel="noreferrer"
            >
              application confirmation email
            </Styled.ActionLink>
          </Styled.ActionItem>
        </Styled.ActionList>
      </Styled.ModalSection>

      <Styled.ButtonContainer>
        <Button styleType="secondary" onClick={onCancel}>
          Dismiss
        </Button>
      </Styled.ButtonContainer>
    </CenterModal>
  );
}

export default JobPublishSuccessModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.DistributionMessage = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_DistributionMessage;
    ${[t.mb(5)]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    line-height: 1.4;
  `;
});

Styled.ShareIconsContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ShareIconsContainer;
    ${[t.mb(6)]}
    display: flex;
    gap: ${t.spacing[2]};
  `;
});

Styled.ShareIconButton = styled.button((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ShareIconButton;
    ${[t.rounded.sm]}
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    border: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[200]};
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover {
      background-color: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
      border-color: ${t.dark ? t.color.gray[500] : t.color.gray[300]};
    }

    svg {
      width: 1rem;
      height: 1rem;
      color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};
    }
  `;
});

Styled.ModalSection = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ModalSection;
    display: flex;
    flex-direction: column;

    p {
      ${[t.text.md, t.mb(3)]}
      color: ${t.dark ? t.color.gray[100] : t.color.black};
    }
  `;
});

Styled.ActionList = styled.ul((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ActionList;
    ${[t.mb(5), t.pl(5)]}
    display: flex;
    flex-direction: column;
    gap: ${t.spacing[2]};
    list-style-type: disc;
  `;
});

Styled.ActionItem = styled.li((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ActionItem;
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    line-height: 1.5;
  `;
});

Styled.ActionLink = styled.a((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ActionLink;
    color: ${t.dark ? t.color.white : t.color.black};
    font-weight: 600;
    text-decoration: underline !important;
    cursor: pointer;

    &:hover {
      color: ${t.dark ? t.color.gray[200] : t.color.gray[800]};
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobPublishSuccessModal_ButtonContainer;
    ${t.mt(4)}
    display: flex;
  `;
});
